import type {MockPageCollaboration} from "../../types";

export const INITIAL_PAGE_COLLABORATION: Record<string, MockPageCollaboration> = {
  materiality: {
    unreadCount: 0,
    messages: [
      {
        id: "msg-1",
        user: "Jenny Zhan",
        message: "I've completed the benchmark comparison. Revenue looks like the right primary benchmark.",
        timestamp: new Date("2026-02-28T10:30:00"),
      },
      {
        id: "msg-2",
        user: "James Evans",
        message: "Agreed. Can you double-check the trivial threshold % though? 5% seems low for this entity size.",
        timestamp: new Date("2026-02-28T14:15:00"),
      },
      {
        id: "msg-3",
        user: "Jenny Zhan",
        message: "Good point, I'll review against prior year and update if needed.",
        timestamp: new Date("2026-02-28T16:00:00"),
      },
    ],
  },
  "fsa:expenditures:scoping": {
    unreadCount: 0,
    messages: [
      {
        id: "msg-4",
        user: "James Evans",
        message: "Scoping is complete. All COTABDs identified and mapped to assertions.",
        timestamp: new Date("2026-03-01T09:00:00"),
      },
      {
        id: "msg-5",
        user: "Phoebe Scott",
        message: "Looks good. Ready for review.",
        timestamp: new Date("2026-03-01T11:30:00"),
      },
    ],
  },
  "fsa:revenue:scoping": {
    unreadCount: 0,
    messages: [
      {
        id: "msg-6",
        user: "Phoebe Scott",
        message: "Revenue recognition COTABD needs an additional assertion mapping for cutoff.",
        timestamp: new Date("2026-03-02T08:45:00"),
      },
      {
        id: "msg-7",
        user: "Jenny Zhan",
        message: "Added. Also flagged the new revenue stream from Q4.",
        timestamp: new Date("2026-03-02T10:00:00"),
      },
      {
        id: "msg-8",
        user: "Phoebe Scott",
        message: "Perfect, that covers it. Moving to risk assessment next.",
        timestamp: new Date("2026-03-02T10:30:00"),
      },
      {
        id: "msg-9",
        user: "James Evans",
        message: "Make sure to link the new stream to the correct process.",
        timestamp: new Date("2026-03-02T14:00:00"),
      },
    ],
  },
  "fsa:revenue:risk-assessment": {
    unreadCount: 0,
    messages: [
      {
        id: "msg-fsa-rev-ra-1",
        user: "Jenny Zhan",
        message: "Risk assessment draft uploaded. Two significant risks identified around cutoff and valuation.",
        timestamp: new Date("2026-03-05T09:00:00"),
      },
      {
        id: "msg-fsa-rev-ra-2",
        user: "Phoebe Scott",
        message: "Can we add the new contract modification risk? It came up in the Q4 review.",
        timestamp: new Date("2026-03-05T14:30:00"),
      },
    ],
  },
  "elc:elc-control-environment:questionnaires": {
    unreadCount: 0,
    messages: [
      {
        id: "msg-ce-1",
        user: "Jenny Zhan",
        message: "Board oversight walkthrough and control environment assessment both completed. Ready for review.",
        timestamp: new Date("2026-02-12T10:00:00"),
      },
      {
        id: "msg-ce-2",
        user: "Phoebe Scott",
        message: "Reviewed. Ethics training completion at 94% — noted as a low finding. Everything else looks solid.",
        timestamp: new Date("2026-02-13T14:30:00"),
      },
    ],
  },
  "elc:elc-it-environment:questionnaires": {
    unreadCount: 1,
    messages: [
      {
        id: "msg-10",
        user: "James Evans",
        message: "IT environment overview drafted. Need input on the new cloud migration.",
        timestamp: new Date("2026-03-01T15:00:00"),
      },
      {
        id: "msg-11",
        user: "Jenny Zhan",
        message: "I'll add notes on the AWS to GCP migration timeline.",
        timestamp: new Date("2026-03-01T16:30:00"),
      },
    ],
  },
  "elc:elc-it-environment:risks": {
    unreadCount: 2,
    messages: [
      {
        id: "msg-it-rc-1",
        user: "Phoebe Scott",
        message: "Two access control exceptions found during quarterly review. Remediation tickets created.",
        timestamp: new Date("2026-03-03T10:00:00"),
      },
      {
        id: "msg-it-rc-2",
        user: "James Evans",
        message: "Contractor deprovisioning SLA needs to be tightened. Adding as a finding.",
        timestamp: new Date("2026-03-03T11:30:00"),
      },
      {
        id: "msg-it-rc-3",
        user: "Phoebe Scott",
        message: "Agreed. I'll draft the finding and link the access review evidence.",
        timestamp: new Date("2026-03-03T14:00:00"),
      },
    ],
  },
  "elc:elc-risk-assessment:understanding": {
    unreadCount: 0,
    messages: [
      {
        id: "msg-ra-u-1",
        user: "Jenny Zhan",
        message:
          "Risk register reviewed — six business risks mapped to financial reporting assertions. Two still need mitigation plans.",
        timestamp: new Date("2026-03-04T09:00:00"),
      },
      {
        id: "msg-ra-u-2",
        user: "Phoebe Scott",
        message: "Which two are outstanding? We should flag them for the partner meeting.",
        timestamp: new Date("2026-03-04T15:00:00"),
      },
    ],
  },
  "process:proc-vendor-invoice:understanding": {
    unreadCount: 0,
    messages: [
      {
        id: "msg-12",
        user: "Jenny Zhan",
        message: "Vendor invoice process is fully documented and locked.",
        timestamp: new Date("2026-02-25T09:00:00"),
      },
      {
        id: "msg-13",
        user: "James Evans",
        message: "Confirmed. All controls tested and evidence linked.",
        timestamp: new Date("2026-02-25T14:00:00"),
      },
    ],
  },
  "process:proc-expense-reimbursement:controls": {
    unreadCount: 0,
    messages: [
      {
        id: "msg-er-rc-1",
        user: "Phoebe Scott",
        message: "Receipt validation control needs additional testing — the $75 threshold exception isn't documented.",
        timestamp: new Date("2026-03-06T10:00:00"),
      },
      {
        id: "msg-er-rc-2",
        user: "Jenny Zhan",
        message: "I'll add the policy exception documentation and re-test.",
        timestamp: new Date("2026-03-06T14:00:00"),
      },
    ],
  },
  "process:proc-order-fulfillment:understanding": {
    unreadCount: 2,
    messages: [
      {
        id: "msg-of-u-1",
        user: "James Evans",
        message: "Billing trigger walkthrough completed. Need to verify configuration with the warehouse team.",
        timestamp: new Date("2026-03-07T09:00:00"),
      },
      {
        id: "msg-of-u-2",
        user: "Phoebe Scott",
        message: "The shipping confirmation to invoice timing gap is concerning — averaging 3 days.",
        timestamp: new Date("2026-03-07T11:00:00"),
      },
      {
        id: "msg-of-u-3",
        user: "James Evans",
        message: "Agreed, that's a cutoff risk. I'll flag it in the risk assessment.",
        timestamp: new Date("2026-03-07T14:30:00"),
      },
    ],
  },
};
